import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { Presence } from '@/components/core/presence-avatar/presence-avatar';
import { HRM_PERMISSIONS } from '@/constant/hr/permissions';
import { useHasPermission } from '@/hooks/use-has-permission';
import { presenceText } from '../utils/employee-presence';
import { useEmployeesPresence } from './use-employee-presence';

/** What a `PersonAvatar` needs of one person's presence; empty while it is unknown. */
export interface PersonPresenceProps {
  presence?: Presence;
  presenceLabel?: string;
}

/**
 * Today's presence of any people, for `PersonAvatar` wherever a person is shown (People, the leave
 * calendar, dialogs). It reads only for viewers who may read everyone's attendance (as People does), so
 * for everyone else it runs no query and every avatar is the plain one. Use `presenceOf(userId)`:
 *
 *   const { presenceOf } = usePersonPresence(userIds);
 *   <PersonAvatar name={name} {...presenceOf(userId)} />
 */
export const usePersonPresence = (userIds: string[]) => {
  const { t } = useTranslation();
  const canSee = useHasPermission(HRM_PERMISSIONS.TIME_ATTENDANCE.ATTENDANCE_VIEW_ALL);
  const { byUserId, isLoading } = useEmployeesPresence(userIds, canSee);

  const presenceOf = useCallback(
    (userId: string): PersonPresenceProps => {
      const view = byUserId[userId];
      if (!canSee || isLoading || !view) return {};
      return {
        presence: view.presence,
        presenceLabel: `${presenceText(t, view.label)} · ${presenceText(t, view.detail)}`,
      };
    },
    [byUserId, canSee, isLoading, t]
  );

  return useMemo(() => ({ presenceOf, isAvailable: canSee }), [presenceOf, canSee]);
};
